/**
 * ---------------------------------------------------------------------------------------------------------------------
 * SUBSCRIPTION UPGRADE (custom)
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Markup: snippets/line-item-subscription-upgrade.liquid (rendered by the "line-item" snippet)
 * Settings: Theme settings > Cart > Subscription upgrade
 *
 * - One-time purchase: the button switches the line item to the first subscription plan ("Upgrading..." while loading)
 * - Subscription: the dropdown changes the delivery frequency (selling plan) of the line item
 *
 * The line item is updated in place with /cart/change.js (same line, new selling plan). The theme's sections are
 * bundled in the same request (cart:prepare-bundled-sections) so no extra fetch is needed:
 *
 * - Cart drawer: we dispatch the theme's "cart:change" event, the drawer re-renders itself. A CUSTOM edit in theme.js
 *   (CartDrawer._onCartChanged) skips the theme's 1250ms delay when baseEvent is "subscription-upgrade:change".
 * - Cart page: the theme reloads the page on cart changes, so instead we patch the cart page from the bundled section.
 */
import { extractSectionId } from "theme";

const CHANGE_EVENT_NAME = "subscription-upgrade:change";

class SubscriptionUpgrade extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this.addEventListener("click", this._onClick.bind(this), { signal });
    this.addEventListener("keydown", this._onKeyDown.bind(this), { signal });
    document.addEventListener("click", this._onDocumentClick.bind(this), { signal });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  get toggleButton() {
    return this.querySelector(".subscription-upgrade__toggle");
  }

  get listbox() {
    return this.querySelector('[role="listbox"]');
  }

  get options() {
    return Array.from(this.querySelectorAll('[role="option"]'));
  }

  get selectedOption() {
    return this.options.find((option) => option.getAttribute("aria-selected") === "true") || this.options[0];
  }

  get isBusy() {
    return this.getAttribute("aria-busy") === "true";
  }

  get isOpen() {
    return this.toggleButton?.getAttribute("aria-expanded") === "true";
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------
   * DROPDOWN
   * ---------------------------------------------------------------------------------------------------------------
   */

  open(optionToFocus = null) {
    if (!this.listbox || this.isBusy) {
      return;
    }

    this.listbox.hidden = false;
    this.toggleButton.setAttribute("aria-expanded", "true");
    optionToFocus?.focus();
  }

  close(focusToggle = false) {
    if (!this.listbox || !this.isOpen) {
      return;
    }

    this.listbox.hidden = true;
    this.toggleButton.setAttribute("aria-expanded", "false");

    if (focusToggle) {
      this.toggleButton.focus();
    }
  }

  _onClick(event) {
    if (this.isBusy) {
      return;
    }

    const option = event.target.closest('[role="option"]');

    if (option) {
      return this._onOptionSelected(option);
    }

    const toggleButton = event.target.closest(".subscription-upgrade__toggle");

    if (!toggleButton) {
      return;
    }

    if (toggleButton.hasAttribute("data-selling-plan-id")) {
      this._onUpgradeClicked(toggleButton);
    } else {
      this.isOpen ? this.close() : this.open();
    }
  }

  _onDocumentClick(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  _onKeyDown(event) {
    if (!this.listbox || this.isBusy) {
      return;
    }

    const options = this.options;
    const currentIndex = options.indexOf(document.activeElement);
    const isOnToggle = document.activeElement === this.toggleButton;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();

        if (isOnToggle || !this.isOpen) {
          this.open(event.key === "ArrowDown" ? this.selectedOption : options[options.length - 1]);
        } else if (currentIndex !== -1) {
          const nextIndex = (currentIndex + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
          options[nextIndex].focus();
        }
        break;

      case "Home":
      case "End":
        if (this.isOpen && currentIndex !== -1) {
          event.preventDefault();
          (event.key === "Home" ? options[0] : options[options.length - 1]).focus();
        }
        break;

      case "Enter":
      case " ":
        if (currentIndex !== -1) {
          event.preventDefault();
          this._onOptionSelected(options[currentIndex]);
        }
        break;

      case "Escape":
        if (this.isOpen) {
          event.preventDefault();
          this.close(true);
        }
        break;

      case "Tab":
        this.close();
        break;
    }
  }

  /**
   * ---------------------------------------------------------------------------------------------------------------
   * SELLING PLAN CHANGE
   * ---------------------------------------------------------------------------------------------------------------
   */

  _onUpgradeClicked(toggleButton) {
    this._saveDropdownState();

    // The button immediately takes the look of the frequency dropdown, with the "Upgrading..." text
    toggleButton.classList.remove("subscription-upgrade__toggle--upgrade", "justify-center");
    toggleButton.classList.add("justify-between");
    toggleButton.querySelector(".subscription-upgrade__text").textContent = this.getAttribute("loading-text");

    this._changeSellingPlan(toggleButton.getAttribute("data-selling-plan-id"));
  }

  _onOptionSelected(option) {
    if (option.getAttribute("aria-selected") === "true") {
      return this.close(true);
    }

    this._saveDropdownState();

    // Optimistic update: the new frequency is shown right away while the cart is updated
    this.options.forEach((item) => item.setAttribute("aria-selected", item === option ? "true" : "false"));
    this.toggleButton.querySelector(".subscription-upgrade__frequency").textContent = option.querySelector(".subscription-upgrade__frequency").textContent;
    this.close(true);

    this._changeSellingPlan(option.getAttribute("data-selling-plan-id"));
  }

  /**
   * Keep the markup of the button / dropdown before it is updated, so it can be restored if the request fails
   */
  _saveDropdownState() {
    this._previousDropdownHtml = this.querySelector(".subscription-upgrade__dropdown").innerHTML;
  }

  async _changeSellingPlan(sellingPlanId) {
    if (this.isBusy || !sellingPlanId) {
      return;
    }

    const dropdown = this.querySelector(".subscription-upgrade__dropdown");
    const row = this.closest("tr") || this.closest("line-item");
    const cartDrawer = this.closest("cart-drawer");
    const linePosition = this.getAttribute("line-position");
    const quantityInputs = Array.from(row?.querySelectorAll(".quantity-input") || []);

    this.setAttribute("aria-busy", "true");
    this._announce(this.getAttribute("loading-text"));
    this.querySelector('[role="alert"]')?.remove();
    quantityInputs.forEach((input) => input.disabled = true);

    // Ask the theme which sections need to be re-rendered (the cart drawer adds itself), and add the cart page
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

    const mainCartSectionId = !cartDrawer && this.closest(".shopify-section") ? extractSectionId(this) : null;

    if (mainCartSectionId && !sectionsToBundle.includes(mainCartSectionId)) {
      sectionsToBundle.push(mainCartSectionId);
    }

    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: this.getAttribute("line-key"),
          quantity: parseInt(this.getAttribute("quantity")),
          selling_plan: parseInt(sellingPlanId),
          sections: sectionsToBundle
        })
      });

      const responseContent = await response.json();

      if (!response.ok) {
        throw new Error(responseContent["description"] || responseContent["message"]);
      }

      const hadFocus = this.contains(document.activeElement);
      const container = cartDrawer || this.closest(".shopify-section");

      if (mainCartSectionId && responseContent["sections"]?.[mainCartSectionId]) {
        this._renderMainCart(responseContent["sections"][mainCartSectionId]);
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: CHANGE_EVENT_NAME,
          cart: responseContent
        }
      }));

      // The cart drawer re-renders on the next tick: give the focus back to the same line for keyboard users
      if (hadFocus) {
        setTimeout(() => container?.querySelector(`subscription-upgrade[line-position="${linePosition}"] .subscription-upgrade__toggle`)?.focus());
      }
    } catch (error) {
      dropdown.innerHTML = this._previousDropdownHtml;
      this.removeAttribute("aria-busy");
      this._announce("");
      quantityInputs.forEach((input) => input.disabled = false);
      this._showError(error.message);
      this.toggleButton?.focus();
    }
  }

  /**
   * The cart page has no cart:change listener (the theme reloads the page), so we update the parts that change: the
   * free shipping bar, the line items and the totals. The totals are patched (not replaced) so that content injected
   * by apps (such as the Sezzle installments) is kept.
   */
  _renderMainCart(html) {
    const updatedDocument = new DOMParser().parseFromString(html, "text/html");

    [".cart-header", ".order-summary__body"].forEach((selector) => {
      const current = document.querySelector(selector), updated = updatedDocument.querySelector(selector);

      if (current && updated) {
        current.replaceChildren(...updated.childNodes);
      }
    });

    document.querySelectorAll(".cart-form__totals").forEach((current, index) => {
      const updated = updatedDocument.querySelectorAll(".cart-form__totals")[index];

      if (updated) {
        this._patchElement(current, updated);
      }
    });
  }

  _patchElement(current, updated) {
    const currentChildren = Array.from(current.children), updatedChildren = Array.from(updated.children);

    if (updatedChildren.length === 0) {
      if (current.innerHTML !== updated.innerHTML) {
        current.innerHTML = updated.innerHTML;
      }

      return;
    }

    // Match each server-rendered child with the current one, skipping elements injected by apps
    const isSameElement = (a, b) => a.tagName === b.tagName && a.getAttribute("class") === b.getAttribute("class");
    const matches = [];
    let currentIndex = 0;

    for (const updatedChild of updatedChildren) {
      while (currentIndex < currentChildren.length && !isSameElement(currentChildren[currentIndex], updatedChild)) {
        currentIndex++;
      }

      if (currentIndex === currentChildren.length) {
        return current.replaceChildren(...updated.childNodes); // Structure changed (e.g. a discount was added)
      }

      matches.push([currentChildren[currentIndex++], updatedChild]);
    }

    matches.forEach(([currentChild, updatedChild]) => this._patchElement(currentChild, updatedChild));
  }

  _showError(message) {
    const errorElement = document.createElement("p");
    errorElement.className = "banner banner--with-icon banner--error banner--sm w-full";
    errorElement.setAttribute("role", "alert");
    errorElement.innerHTML = `<svg width="18" viewBox="0 0 18 18">
      <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
      <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
    </svg>`;
    errorElement.append(` ${message || ""}`);
    this.append(errorElement);
  }

  _announce(message) {
    const statusElement = this.querySelector('[role="status"]');

    if (statusElement) {
      statusElement.textContent = message;
    }
  }
}

if (!window.customElements.get("subscription-upgrade")) {
  window.customElements.define("subscription-upgrade", SubscriptionUpgrade);
}
